/**
 * Carichi di squadra (V68): in cima la dose della rosa e l'ACWR di squadra, come
 * in V69 e con lo stesso filtro per categoria, che vale anche per il grafico dei
 * giocatori; poi due sezioni di grafici senza card (dose per
 * attributo; barre della rosa e linee dei giocatori accesi) e le card dose/ACWR
 * per ruolo, una sotto l'altra con le righe su due colonne sugli schermi larghi
 * e senza pagine: la rosa di un ruolo sta tutta nella card. Dirigente compreso.
 */

import { useEffect, useMemo, useState, type ComponentProps } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, XAxis, YAxis } from 'recharts';
import {
  ETICHETTA_ACWR,
  ETICHETTA_FAMIGLIA_ATTRIBUTO,
  FINESTRE_CARICO,
  type LetturaAcwr,
  RUOLI_GIOCATORE,
} from '@calcio/shared';
import { ActivityIcon, ChartColumnIcon, UsersIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { StatoCaricamento, Testata } from '../comuni/Testata.js';
import { Corpo } from '../comuni/Corpo.js';
import { SezioneGrafici } from '../comuni/Riquadri.js';
import { LegendaEvidenziabile } from '../comuni/LegendaEvidenziabile.js';
import { useDati } from '../comuni/dati.js';
import { REPARTI } from '../giocatori/elenchi.js';
import {
  coloreSerie,
  configSerie,
  pallinoSeSolo,
  serieGiocatori,
  classeSerie,
} from '../valutazioni/graficiConfronto.js';
import { CampoIntervallo } from '../comuni/CampoIntervallo.js';
import { SceltaGiocatori } from '../comuni/SceltaGiocatori.js';
import { Maglia } from '../comuni/Maglia.js';
import {
  type CarichiSquadra as Dati,
  type GiocatoreCaricoSquadra,
  COLORE_LETTURA,
  coloreAcwr,
  letturaAcwr,
} from './carichi.js';
import { RiassuntoPeriodo } from './RiassuntoPeriodo.js';
import {
  ETICHETTA_MODO,
  type FiltroFamigliaAttributo,
  type ModoFinestra,
  doseDelFiltro,
  etichettaAsse,
  famigliePresenti,
  filtraAttributi,
  idsAccesiIniziali,
  MODO_FINESTRA_DEFAULT,
  ordinaPerCognome,
  ordinaVociTooltip,
  percorsoCarichiSquadra,
  righeGrafico,
  serieDelFiltro,
  soloConCarico,
} from './serieSquadra.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';

const CONFIG_SQUADRA: ChartConfig = {
  doseSquadra: { label: 'Squadra', color: 'var(--muted-foreground)' },
};

const CONFIG_ATTRIBUTI: ChartConfig = {
  ua: { label: 'Dose', color: 'var(--chart-1)' },
};

function TooltipDose({
  tick,
  payload,
  ...rest
}: ComponentProps<typeof ChartTooltipContent> & { tick: (chiave: string) => string }) {
  return (
    <ChartTooltipContent
      {...rest}
      payload={ordinaVociTooltip(payload ?? [])}
      indicator="dot"
      labelFormatter={(v) => (typeof v === 'string' ? tick(v) : null)}
    />
  );
}

export function CarichiSquadra() {
  const { ss } = useParams<{ ss: string }>();
  return <Elenco key={ss} ss={ss!} />;
}

function Elenco({ ss }: { ss: string }) {
  const [modo, setModo] = useState<ModoFinestra>(MODO_FINESTRA_DEFAULT);
  const [da, setDa] = useState('');
  const [a, setA] = useState('');
  const [accesi, setAccesi] = useState<string[]>([]);
  const [famiglia, setFamiglia] = useState<FiltroFamigliaAttributo>('tutti');
  const percorso = percorsoCarichiSquadra(ss, modo, da, a);
  const { dati, errore } = useDati<Dati>(percorso, { tieni: true });

  const rosa = useMemo(
    () => (dati ? ordinaPerCognome([...dati.portieri, ...dati.movimento]) : []),
    [dati],
  );

  useEffect(() => {
    if (rosa.length === 0) return;
    const ids = rosa.map((g) => g.id);
    setAccesi((prev) => {
      if (prev.length === 0) return idsAccesiIniziali(rosa);
      const restano = prev.filter((id) => ids.includes(id));
      return restano.length > 0 ? restano : idsAccesiIniziali(rosa);
    });
  }, [rosa]);

  if (!dati) {
    return (
      <>
        <Testata titolo="Carichi" voce="carichi" />
        <Corpo>
          <StatoCaricamento errore={errore} />
        </Corpo>
      </>
    );
  }

  const settimanale = modo === 'stagione';
  const serie = serieGiocatori(
    rosa.map((g) => g.id),
    rosa,
  );
  const serieAccese = serie.filter((s) => accesi.includes(s.id));
  const config = { ...CONFIG_SQUADRA, ...configSerie(serieAccese) };
  const tick = (chiave: string) => etichettaAsse(chiave, settimanale);
  const famiglie = famigliePresenti(dati.attributi);
  const filtro: FiltroFamigliaAttributo =
    famiglia === 'tutti' || famiglie.includes(famiglia) ? famiglia : 'tutti';
  // Barre della rosa e linee dei giocatori seguono il filtro per categoria.
  const righe = soloConCarico(
    righeGrafico(
      dati.asse,
      rosa.map((g) => ({ serieDose: serieDelFiltro(g.serieDose, g.serieDosePerFamiglia, filtro) })),
    ),
  );
  const attributi = filtraAttributi(dati.attributi, filtro);

  return (
    <>
      <Testata titolo="Carichi" voce="carichi" />
      <Corpo>
        <div className="flex flex-col gap-8">
          <RiassuntoPeriodo
            dose={doseDelFiltro(dati.doseUa, dati.attributi, filtro)}
            totale={dati.doseUa}
            famiglia={filtro}
            acwr={dati.acwr}
            avviso={
              dati.sottoTarget > 0
                ? `${dati.sottoTarget === 1 ? 'Un giocatore ha' : `${dati.sottoTarget} giocatori hanno`} tre sedute consecutive sotto il RPE target negli ultimi 14 giorni.`
                : undefined
            }
          />
          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
              type="single"
              variant="outline"
              aria-label="Finestra"
              value={modo === 'personalizzato' ? '' : modo}
              onValueChange={(v) => v && setModo(v as ModoFinestra)}
            >
              {FINESTRE_CARICO.map((f) => (
                <ToggleGroupItem key={f} value={f}>
                  {ETICHETTA_MODO[f]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
            <CampoIntervallo
              id="carichi-intervallo"
              da={dati.da}
              a={dati.a}
              onCambia={(dal, al) => {
                setDa(dal);
                setA(al);
                setModo('personalizzato');
              }}
            />
          </div>
          <SezioneGrafici
            titolo="Categoria"
            Icona={ChartColumnIcon}
            azione={
              famiglie.length > 0 && (
                <ToggleGroup
                  type="single"
                  variant="outline"
                  className="flex-wrap"
                  value={filtro}
                  onValueChange={(v) => v && setFamiglia(v as FiltroFamigliaAttributo)}
                >
                  <ToggleGroupItem value="tutti">Tutti</ToggleGroupItem>
                  {famiglie.map((f) => (
                    <ToggleGroupItem key={f} value={f}>
                      {ETICHETTA_FAMIGLIA_ATTRIBUTO[f]}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              )
            }
          >
            {attributi.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyTitle>Nessun carico</EmptyTitle>
                  <EmptyDescription>
                    Nessuna dose per attributo in questa finestra.
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <ChartContainer config={CONFIG_ATTRIBUTI} className="aspect-auto h-80 w-full">
                <BarChart data={attributi} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="nome"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    interval={0}
                    angle={-40}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40} />
                  <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />
                  <Bar dataKey="ua" radius={4}>
                    {attributi.map((a) => (
                      <Cell
                        key={a.attributoId}
                        fill={coloreSerie(
                          dati.attributi.findIndex((x) => x.attributoId === a.attributoId),
                        )}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ChartContainer>
            )}
          </SezioneGrafici>
          <SezioneGrafici
            titolo="Giocatori"
            Icona={UsersIcon}
            azione={
              <SceltaGiocatori
                id="carichi-giocatori"
                rosa={rosa}
                scelti={accesi}
                colore={(id) => coloreSerie(rosa.findIndex((g) => g.id === id))}
                onCambia={setAccesi}
              />
            }
          >
            {rosa.length === 0 ? (
              <Empty>
                <EmptyHeader>
                  <EmptyTitle>Nessun giocatore</EmptyTitle>
                  <EmptyDescription>Nessun giocatore in rosa in questa finestra.</EmptyDescription>
                </EmptyHeader>
              </Empty>
            ) : (
              <ChartContainer
                config={config}
                className="aspect-auto w-full"
                style={{ height: 256 + Math.floor((serieAccese.length + 1) / 4) * 20 }}
              >
                <ComposedChart data={righe} margin={{ top: 8, right: 16 }}>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="chiave"
                    tickFormatter={tick}
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={24}
                  />
                  <YAxis
                    yAxisId="giocatore"
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <YAxis
                    yAxisId="squadra"
                    orientation="right"
                    allowDecimals={false}
                    tickLine={false}
                    axisLine={false}
                    width={40}
                  />
                  <ChartTooltip content={<TooltipDose tick={tick} />} />
                  <ChartLegend
                    wrapperStyle={{ paddingTop: 8 }}
                    content={<LegendaEvidenziabile />}
                  />
                  <Bar
                    yAxisId="squadra"
                    dataKey="doseSquadra"
                    className={classeSerie('doseSquadra')}
                    fill="var(--color-doseSquadra)"
                    radius={4}
                    fillOpacity={0.45}
                  />
                  {serieAccese.map((s) => (
                    <Line
                      key={s.chiave}
                      yAxisId="giocatore"
                      dataKey={s.chiave}
                      className={classeSerie(s.chiave)}
                      type="monotone"
                      stroke={`var(--color-${s.chiave})`}
                      strokeWidth={3}
                      dot={pallinoSeSolo(righe, s.chiave)}
                    />
                  ))}
                </ComposedChart>
              </ChartContainer>
            )}
          </SezioneGrafici>
          <div className="flex flex-col gap-4">
            {RUOLI_GIOCATORE.map((ruolo, i) => (
              <Gruppo
                key={ruolo}
                titolo={REPARTI[i]!}
                righe={[...dati.portieri, ...dati.movimento].filter((g) => g.ruolo === ruolo)}
              />
            ))}
          </div>
        </div>
      </Corpo>
    </>
  );
}

const LETTURE: LetturaAcwr[] = ['zona', 'sotto', 'attenzione', 'rischio'];

function Gruppo({ titolo, righe }: { titolo: string; righe: GiocatoreCaricoSquadra[] }) {
  return (
    <Card className="h-full bg-muted">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-center gap-2 font-semibold tracking-wide uppercase">
          <h2>{titolo}</h2>
          {LETTURE.map((l) => (
            <ConteggioLettura
              key={l}
              lettura={l}
              quanti={righe.filter((g) => g.acwr.stato === 'ok' && g.acwr.lettura === l).length}
            />
          ))}
        </CardTitle>
      </CardHeader>
      <CardContent className="px-2 @xl:px-(--card-spacing)">
        {righe.length === 0 ? (
          <StatoVuoto titolo="Nessun giocatore" />
        ) : (
          <ul className="grid gap-2 @5xl:grid-cols-2">
            {righe.map((g) => (
              <li key={g.id}>
                <RigaCarico giocatore={g} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/** Quanti giocatori della card hanno l'ACWR in una lettura, nel colore della lettura; a zero non compare. */
function ConteggioLettura({ lettura, quanti }: { lettura: LetturaAcwr; quanti: number }) {
  if (quanti === 0) return null;
  const etichetta = `${ETICHETTA_ACWR[lettura]}: ${quanti}`;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge className={cn('tabular-nums', COLORE_LETTURA[lettura])} aria-label={etichetta}>
          {quanti}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>{etichetta}</TooltipContent>
    </Tooltip>
  );
}

/** La maglia, poi nome e dose, e a destra il tassello dell'ACWR con la fascia del colore della lettura: il ruolo è quello della card. */
function RigaCarico({ giocatore: g }: { giocatore: GiocatoreCaricoSquadra }) {
  return (
    <Link
      to={`/giocatori/${g.id}/carichi`}
      className="flex h-full items-center gap-3 rounded-lg border border-border bg-card p-2.5"
    >
      <Maglia numero={g.numero} ruolo={g.ruolo} />

      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-base font-semibold break-words">
            {g.cognome} {g.nome}
          </span>
          <Badge className={coloreAcwr(g.acwr)}>{letturaAcwr(g.acwr)}</Badge>
          {g.serieSottoTarget && <Badge variant="outline">Sotto target</Badge>}
        </span>
        <span className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span className="flex items-center gap-1" title="Dose del periodo">
            <ActivityIcon aria-hidden className="size-4" />
            <span className="font-medium text-foreground tabular-nums">{g.doseUa}</span> UA
          </span>
        </span>
      </span>

      <span className="flex w-14 shrink-0 flex-col self-stretch overflow-hidden rounded-md border border-border bg-background text-center">
        <span className={cn('py-0.5 text-[11px] font-semibold uppercase', coloreAcwr(g.acwr))}>
          ACWR
        </span>
        <span className="flex flex-1 items-center justify-center py-1 text-lg leading-7 font-bold tabular-nums">
          {g.acwr.stato === 'ok' ? g.acwr.valore.toFixed(2) : '—'}
        </span>
      </span>
    </Link>
  );
}
